import { Flex, Box, Tooltip, Message, BoxProps } from 'components/ui';
import styled from 'styled-components/macro';
import { Link } from 'react-router-dom';
import { Row, Col } from 'react-grid-system';
import genIcon from 'img/gen.svg';
import twistsIcon from 'img/twists.svg';
import spiralIcon from 'img/spiral.svg';
import triangleIcon from 'img/triangle.svg';
import squareIcon from 'img/square.svg';
import lineIcon from 'img/line.svg';
import metronome from 'img/metronome.svg';
import { getEffectsList } from 'utils/futura';
import FuturaThemeStructure from './FuturaThemeStructure';
import { FUTURA_PALETTES, FUTURA_ATTRIBUTES } from '../constants';
import { useEffectsState } from 'hooks/useEffectsState';
import FuturaItemHeader from './FuturaItemHeader';
import { OwnedNft } from 'utils/types';

const Section = ({ children }) => (
  <Box background="black" p="8px" maxWidth="800px" borderRadius="16px" mb={3}>
    {children}
  </Box>
);

interface AttributeProps {
  title: string;
  value: any;
  leftSideContent?: JSX.Element;
  icon?: string;
}

const Attribute = ({ title, value, leftSideContent, icon, ...rest }: AttributeProps & BoxProps) => (
  <Flex
    width="100%"
    height="100%"
    p="8px"
    alignSelf="center"
    alignCenter
    justifyCenter
    data-tip="attrtip"
    data-for={`tooltip_${title}`}
    {...rest}
  >
    {leftSideContent
      ? leftSideContent
      : icon && (
          <Box mr={2}>
            <img src={icon} alt={title} width="32px" />
          </Box>
        )}
    <Box>
      <Box fontSize={['11px']} mb={1}>
        {title}
      </Box>
      <Box fontWeight="bold">{value || '?'}</Box>
    </Box>
  </Flex>
);

const AttributeTooltip = ({ id, description }: { id: string; description: string }) => (
  <Tooltip id={id} place="left" effect="solid" delayHide={500} clickable>
    <Box mb={2}>{description}</Box>
    <Flex width="100%" fontWeight="bold" justifyContent="flex-end">
      <Link to="/futura/traits" style={{ color: 'white' }}>
        Learn more
      </Link>
    </Flex>
  </Tooltip>
);

const EffectIcon = styled.div<any>`
  width: 32px;
  margin-right: 8px;
  ${props =>
    props.isActive &&
    `
    transform: scale(1.1);
    filter: brightness(400%);
  `}
`;

const Effect = ({ icon, name, isActive }) => (
  <>
    <Box mr={2} data-for={`tooltip_${name}`} data-tip={`tip_${name}`}>
      <EffectIcon isActive={isActive}>
        <img src={icon} alt={name} width="100%" />
      </EffectIcon>
    </Box>
    <Tooltip id={`tooltip_${name}`} place="left" effect="solid" delayHide={500} clickable>
      <Box mb={2}>{name}</Box>
      <Flex width="100%" fontWeight="bold" justifyContent="flex-end">
        <Link to="/futura/traits" style={{ color: 'white' }}>
          Learn more
        </Link>
      </Flex>
    </Tooltip>
  </>
);

const SHAPE_ICONS = {
  Triangle: triangleIcon,
  Square: squareIcon,
  Line: lineIcon,
};

interface FuturaItemTraitsProps {
  item: OwnedNft;
  pieceData: any;
  tokenId: string;
}

const FuturaItemTraits = ({ item, pieceData, tokenId }: FuturaItemTraitsProps) => {
  const palette =
    FUTURA_PALETTES.find(
      palette => palette.name === item?.metadata?.attributes[FUTURA_ATTRIBUTES.colorPalette]
    ) || FUTURA_PALETTES[0];
  const isRevealed = item?.metadata?.attributes[FUTURA_ATTRIBUTES.colorPalette];
  const distortionValue =
    item?.metadata?.attributes[FUTURA_ATTRIBUTES.distortion] === 'true'
      ? 'Yes'
      : item?.metadata?.attributes[FUTURA_ATTRIBUTES.distortion] === 'false'
      ? 'No'
      : '?';

  const { activeEffects } = useEffectsState();

  const availableEffects = getEffectsList(item?.metadata?.attributes ?? {});

  return (
    <Box>
      <Flex width="100%" alignStart maxWidth="800px" marginBottom={3}>
        <FuturaItemHeader item={item} tokenId={tokenId} />
      </Flex>

      <Section>
        <Row>
          <Col xs={6} md={6} lg={3}>
            <Attribute
              title="Generation"
              value={item?.metadata?.attributes[FUTURA_ATTRIBUTES.generation]}
              icon={genIcon}
            />
            <AttributeTooltip
              id="tooltip_Generation"
              description="Each Futura NFT is considered from a certain generation. Futura Masters are Generation 0; Futura Mixes are Generation 1; Futura Remixes are Generation 2."
            />
          </Col>
          <Col xs={6} md={6} lg={3}>
            <Attribute
              title="Twists"
              value={item?.metadata?.attributes[FUTURA_ATTRIBUTES.twists]}
              icon={twistsIcon}
              borderLeft={['none', '1px solid #333']}
            />
            <AttributeTooltip
              id="tooltip_Twists"
              description="There is a correlation between two global traits: number of Twists and the Generation."
            />
          </Col>
          <Col xs={6} md={6} lg={3}>
            <Attribute
              title="Distortion"
              value={distortionValue}
              icon={spiralIcon}
              borderLeft={['none', '1px solid #333']}
            />
            <AttributeTooltip
              id="tooltip_Distortion"
              description="When a Futura NFT has a distortion the notes will not follow the same rotation axis as the band. They will be slightly offset."
            />
          </Col>
          <Col xs={6} md={6} lg={3}>
            <Attribute
              title="Note Shape"
              value={item?.metadata?.attributes[FUTURA_ATTRIBUTES.noteShape]}
              icon={SHAPE_ICONS[item?.metadata?.attributes[FUTURA_ATTRIBUTES.noteShape]]}
              borderLeft={['none', '1px solid #333']}
            />
            <AttributeTooltip
              id="tooltip_Note Shape"
              description="Each piece has a predefined shape. There are 3 different shapes: Square Line and Triangle."
            />
          </Col>
        </Row>
      </Section>

      {!isRevealed && (
        <Message color="black" maxWidth="800px">
          Your mix is being processed, please check again in a minute.
        </Message>
      )}

      {isRevealed && (
        <>
          <Section>
            <Row>
              <Col xs={6} md={3}>
                <Attribute
                  title="Tempo"
                  value={item?.metadata?.attributes[FUTURA_ATTRIBUTES.bpm] + ' BPM'}
                  icon={metronome}
                />
                <AttributeTooltip
                  id="tooltip_Tempo"
                  description="Indicates the music’s speed/pace or BPM. There are three BPMs present in Futura - 100, 120 and 140."
                />
              </Col>
              <Col xs={6} md={3}>
                <Attribute
                  title="Palette"
                  borderLeft={['none', '1px solid #333']}
                  leftSideContent={
                    <Box mr={3}>
                      {palette.layers.map((color, index) => (
                        <Box
                          key={color + index}
                          background={color}
                          width="24px"
                          height="4px"
                          mb={1}
                        />
                      ))}
                    </Box>
                  }
                  value={<Box>{item?.metadata?.attributes[FUTURA_ATTRIBUTES.colorPalette]}</Box>}
                />
                <AttributeTooltip
                  id="tooltip_Palette"
                  description="The color palette is a global trait, with each piece having a predefined color palette."
                />
              </Col>
              <Col sm={12} md={6}>
                <Attribute
                  title="Effects"
                  borderLeft={['none', '1px solid #333']}
                  value={
                    <Flex mt={2} wrap="true">
                      {availableEffects.length === 0 ? (
                        <Box>None</Box>
                      ) : (
                        availableEffects.map(({ name, icon }) => (
                          <Effect
                            icon={icon}
                            name={name}
                            isActive={activeEffects[name]}
                            key={name}
                          />
                        ))
                      )}
                    </Flex>
                  }
                />
              </Col>
            </Row>
          </Section>

          <FuturaThemeStructure
            elementId={`futura_${tokenId}`}
            pieceData={pieceData}
            palette={palette}
          />
        </>
      )}
    </Box>
  );
};

export default FuturaItemTraits;
